import React from 'react';
import { Shield, FileText, Clock, MapPin, Mail, Phone, Users, AlertTriangle, Zap } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1B4F72] to-[#138808] text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-10 h-10" />
          </div>
          <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-xl text-gray-100 max-w-2xl mx-auto leading-relaxed">
            Your trust is our priority. We protect your data with enterprise-grade security and comply with India's DPDP Act 2023.
          </p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Data Collection */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B4F72] mb-6 flex items-center gap-3">
              <Shield className="w-8 h-8" />
              Data We Collect
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">During Registration</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start"><span className="w-2 h-2 bg-[#FF9933] rounded-full mt-2 mr-3 flex-shrink-0"></span>Phone number & OTP verification</li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-[#FF9933] rounded-full mt-2 mr-3 flex-shrink-0"></span>Email address (optional)</li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-[#FF9933] rounded-full mt-2 mr-3 flex-shrink-0"></span>Basic profile info</li>
                </ul>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Journey Data</h3>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-start"><span className="w-2 h-2 bg-[#FF9933] rounded-full mt-2 mr-3 flex-shrink-0"></span>Pickup/drop locations</li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-[#FF9933] rounded-full mt-2 mr-3 flex-shrink-0"></span>Travel history (anonymized)</li>
                  <li className="flex items-start"><span className="w-2 h-2 bg-[#FF9933] rounded-full mt-2 mr-3 flex-shrink-0"></span>Payment details (tokenized)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Data Usage */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B4F72] mb-6 flex items-center gap-3">
              <Zap className="w-8 h-8" />
              How We Use Your Data
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-gray-600 leading-relaxed">
              <ul className="space-y-3">
                <li>• Route optimization & ETA predictions</li>
                <li>• Service matching & booking confirmation</li>
                <li>• Payment processing & refunds</li>
                <li>• Customer support & dispute resolution</li>
              </ul>
              <ul className="space-y-3">
                <li>• Aggregate insights for operators (anonymized)</li>
                <li>• Regulatory compliance reporting</li>
                <li>• Service improvements & new features</li>
                <li>• Never sold to third parties</li>
              </ul>
            </div>
          </div>

          {/* Security */}
          <div className="bg-gradient-to-r from-[#1B4F72] to-[#138808] rounded-2xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              Enterprise-Grade Security
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-6 bg-white/10 rounded-xl">
                <div className="w-12 h-12 bg-white/20 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <Shield className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">End-to-End Encryption</h3>
                <p className="text-sm opacity-90">AES-256 for data at rest, TLS 1.3 in transit</p>
              </div>
              <div className="text-center p-6 bg-white/10 rounded-xl">
                <div className="w-12 h-12 bg-white/20 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <Zap className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">PCI DSS Compliant</h3>
                <p className="text-sm opacity-90">Payment data never stored on our servers</p>
              </div>
              <div className="text-center p-6 bg-white/10 rounded-xl">
                <div className="w-12 h-12 bg-white/20 rounded-2xl mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-6 h-6" />
                </div>
                <h3 className="font-semibold mb-2">DPDP Act 2023</h3>
                <p className="text-sm opacity-90">Full compliance with India's data protection law</p>
              </div>
            </div>
          </div>

          {/* Your Rights */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B4F72] mb-6">Your Rights</h2>
            <ul className="grid md:grid-cols-2 gap-4 text-gray-600">
              <li className="flex items-start p-4 bg-gray-50 rounded-xl">
                <span className="w-2 h-2 bg-[#138808] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <div>
                  <strong>Access:</strong> Download your data anytime
                </div>
              </li>
              <li className="flex items-start p-4 bg-gray-50 rounded-xl">
                <span className="w-2 h-2 bg-[#138808] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <div>
                  <strong>Delete:</strong> Permanent data erasure on request
                </div>
              </li>
              <li className="flex items-start p-4 bg-gray-50 rounded-xl">
                <span className="w-2 h-2 bg-[#138808] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <div>
                  <strong>Rectify:</strong> Update inaccurate information
                </div>
              </li>
              <li className="flex items-start p-4 bg-gray-50 rounded-xl">
                <span className="w-2 h-2 bg-[#138808] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                <div>
                  <strong>Consent:</strong> Withdraw permissions anytime
                </div>
              </li>
            </ul>
          </div>

          <div className="text-center pt-12 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Last updated: December 27, 2025 | Effective immediately
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Questions? <a href="/contact" className="text-[#FF9933] hover:underline font-medium">Contact us</a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
